import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  useTheme,
  TextField,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  CardActions,
  Chip,
  Stack,
  LinearProgress,
  Avatar,
  IconButton,
  Divider,
  Fade,
  Zoom,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  CloudUpload as CloudUploadIcon,
  Description as FileIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  Visibility as PreviewIcon,
  DataArray as DataIcon,
  Upload as UploadIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import Papa from "papaparse";
import { uploadData } from "../../utils/api";
import ExperimentTypeSelector from "../experiment/ExperimentTypeSelector";

export default function SplitDataPage() {
  const { datasetName } = useParams();
  const CSVMeta = JSON.parse(sessionStorage.getItem("CSVMeta") || "{}");
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const [splitFile, setSplitFile] = useState(null);
  const [parsedSplitCSV, setParsedSplitCSV] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [alert, setAlert] = useState("");
  const [customFileName, setCustomFileName] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Enhanced drag and drop handlers
  const handleFileSelection = (file) => {
    if (!file.name.endsWith(".csv")) {
      setAlert("Please select a CSV file");
      return;
    }

    setSplitFile(file);
    const reader = new FileReader();
    reader.onload = ({ target }) => {
      Papa.parse(target.result, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => setParsedSplitCSV(results.data),
        error: (err) => setAlert(`Parsing error: ${err.message}`),
      });
    };
    reader.readAsText(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelection(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileSelection(file);
    }
  };

  const handleUploadSplit = async () => {
    if (!splitFile) return;
    if (customFileName && /\.csv$/i.test(customFileName.trim())) {
      setAlert(t("splitData.CustomFileNameError"));
      return;
    }
    setUploading(true);
    setAlert("");
    setUploadProgress(0);

    const progressInterval = setInterval(() => {
      setUploadProgress((prev) => Math.min(prev + 10, 90));
    }, 100);

    const formData = new FormData();
    formData.append("file", splitFile);
    if (customFileName) formData.append("customFileName", customFileName);

    try {
      const res = await uploadData(
        `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasetName}/split-data`,
        formData
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (res.status === 201) {
        setAlert(
          `File '${customFileName || "split.csv"}' uploaded successfully!`
        );
        setUploadedFiles((prev) => [...prev, customFileName || "split.csv"]);
        setSplitFile(null);
        setParsedSplitCSV([]);
        setCustomFileName("");
        setTimeout(() => setUploadSuccess(true), 1000);
      } else {
        const errorMessage =
          res.detail || `Upload failed. Status: ${res.status}`;
        setAlert(errorMessage);
      }
    } catch (err) {
      clearInterval(progressInterval);
      setUploadProgress(0);
      setAlert(`Network error: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const removeFile = () => {
    setSplitFile(null);
    setParsedSplitCSV([]);
    setCustomFileName("");
  };

  useEffect(() => {
    if (!datasetName || !CSVMeta) {
      navigate("/");
    }
  }, [datasetName, CSVMeta, navigate]);

  if (uploadSuccess) {
    return (
      <Fade in={uploadSuccess}>
        <Box sx={{ p: { xs: 2, md: 4 } }}>
          <Alert
            severity="success"
            sx={{
              mb: 3,
              borderRadius: 2,
              "& .MuiAlert-icon": {
                fontSize: "1.5rem",
              },
            }}
          >
            <Typography variant="h6">
              {t("splitData.SuccessMessage")}
            </Typography>
            <Typography variant="body2">
              {t("splitData.ReadyToConfigure")}
            </Typography>
          </Alert>
          <ExperimentTypeSelector />
        </Box>
      </Fade>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        maxWidth: 1200,
        mx: "auto",
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.grey[50]} 100%)`,
        minHeight: "100vh",
      }}
    >
      {/* -------------------------Header Section------------------------------------- */}
      <Card elevation={0} sx={{ mb: 4, background: "transparent" }}>
        <CardContent sx={{ textAlign: "center", pb: 1 }}>
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              mb: 2,
            }}
          >
            {t("splitData.CreateSplitsTitle", { datasetName })}
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Chip
              icon={<DataIcon />}
              label={`Dataset: ${datasetName}`}
              color="primary"
              variant="outlined"
              sx={{ mr: 1 }}
            />
            {CSVMeta?.fields && (
              <Chip
                label={`${CSVMeta.fields.length} Fields`}
                color="secondary"
                variant="outlined"
              />
            )}
          </Box>

          {CSVMeta?.fields && (
            <Box sx={{ maxWidth: 800, mx: "auto" }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t("splitData.AvailableFields")}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                justifyContent="center"
              >
                {CSVMeta.fields.slice(0, 8).map((field, idx) => (
                  <Chip
                    key={idx}
                    label={field}
                    size="small"
                    variant="outlined"
                    sx={{ mb: 1 }}
                  />
                ))}
                {CSVMeta.fields.length > 8 && (
                  <Chip
                    label={`+${CSVMeta.fields.length - 8} more`}
                    size="small"
                    variant="outlined"
                    sx={{ mb: 1 }}
                  />
                )}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* --------------------------Back Button-------------------------- */}
      <Button
        startIcon={<ArrowBackIcon />}
        variant="outlined"
        onClick={() => navigate(-1)}
        sx={{ mb: 3 }}
      >
        Back to Dataset
      </Button>

      {/* -----------------------Alert Section----------------------- */}
      {alert && (
        <Fade in={!!alert}>
          <Alert
            severity={alert.includes("successfully") ? "success" : "error"}
            sx={{
              mb: 3,
              borderRadius: 2,
              ...(alert.includes("successfully")
                ? {}
                : {
                    bgcolor: "error.main",
                    color: "white",
                    "& .MuiAlert-icon": {
                      color: "white",
                    },
                  }),
            }}
            action={
              <IconButton
                size="small"
                onClick={() => setAlert("")}
                sx={{
                  color: alert.includes("successfully") ? "inherit" : "white",
                }}
              >
                ×
              </IconButton>
            }
          >
            <Typography variant="body1" fontWeight={500}>
              {alert}
            </Typography>
          </Alert>
        </Fade>
      )}

      {/* -------------------------File Upload Section------------------------------------- */}
      <Card elevation={3} sx={{ mb: 4, overflow: "visible" }}>
        <CardContent>
          <Typography
            variant="h5"
            gutterBottom
            sx={{ display: "flex", alignItems: "center", mb: 3 }}
          >
            <CloudUploadIcon
              sx={{ mr: 1, color: theme.palette.primary.main }}
            />
            Upload Split Dataset
          </Typography>

          {/* -------------------------Drag & Drop Area------------------------------------- */}
          <Box
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            sx={{
              border: `3px dashed ${
                dragActive
                  ? theme.palette.primary.main
                  : theme.palette.grey[300]
              }`,
              borderRadius: 3,
              p: 4,
              textAlign: "center",
              bgcolor: dragActive
                ? theme.palette.primary.light
                : theme.palette.grey[50],
              transition: "all 0.3s ease",
              cursor: "pointer",
              mb: 3,
              "&:hover": {
                borderColor: theme.palette.primary.main,
                bgcolor: theme.palette.primary.light,
              },
            }}
            onClick={() => document.getElementById("file-upload-input").click()}
          >
            <input
              id="file-upload-input"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />

            <CloudUploadIcon
              sx={{ fontSize: 64, color: theme.palette.grey[400], mb: 2 }}
            />
            <Typography variant="h6" gutterBottom>
              {dragActive
                ? t("splitData.DropYourCSVFileHere")
                : t("splitData.DragAndDropCSVFile")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("splitData.SupportsCSVFiles")}
            </Typography>
          </Box>

          {/* -------------------------File Info and Actions------------------------------------- */}
          {splitFile && (
            <Zoom in={!!splitFile}>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent sx={{ pb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Avatar sx={{ bgcolor: theme.palette.success.main, mr: 2 }}>
                      <FileIcon />
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {splitFile.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {(splitFile.size / 1024).toFixed(1)} KB •{" "}
                        {parsedSplitCSV.length} rows
                      </Typography>
                    </Box>
                    <IconButton onClick={removeFile} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  <TextField
                    label={t("splitData.CustomFileName")}
                    placeholder={t("splitData.CustomFileNamePlaceholder")}
                    value={customFileName}
                    onChange={(e) => setCustomFileName(e.target.value)}
                    fullWidth
                    variant="outlined"
                    sx={{ mb: 1 }}
                  />
                  {customFileName.toLowerCase().includes(".csv") && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ ml: 1, mb: 2 }}
                    >
                      {t("splitData.CustomFileNameError")}
                    </Typography>
                  )}

                  {uploading && (
                    <Box sx={{ mb: 2 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <Typography variant="body2" sx={{ mr: 1 }}>
                          {t("splitData.Uploading")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {uploadProgress}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress}
                        sx={{ borderRadius: 1, height: 6 }}
                      />
                    </Box>
                  )}
                </CardContent>
                <CardActions
                  sx={{
                    width: "100%",
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleUploadSplit}
                      disabled={uploading}
                      startIcon={
                        uploading ? (
                          <CircularProgress size={16} />
                        ) : (
                          <UploadIcon />
                        )
                      }
                      sx={{ borderRadius: 2 }}
                    >
                      {uploading
                        ? t("splitData.Uploading")
                        : t("splitData.UploadSplitDataset")}
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      {t("splitData.YouCanUploadMultipleFiles")}
                    </Typography>
                  </Stack>
                </CardActions>
              </Card>
            </Zoom>
          )}
        </CardContent>
      </Card>

      {/* -------------------------Uploaded Files List------------------------------------- */}
      {uploadedFiles.length > 0 && (
        <Card elevation={2} sx={{ mb: 4 }}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center" }}
            >
              <CheckIcon sx={{ mr: 1, color: theme.palette.success.main }} />
              {t("splitData.UploadedSplitFiles")}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List dense>
              {uploadedFiles.map((file, idx) => (
                <ListItem key={idx} sx={{ pl: 0 }}>
                  <Avatar
                    sx={{
                      bgcolor: theme.palette.success.main,
                      mr: 2,
                      width: 32,
                      height: 32,
                    }}
                  >
                    <CheckIcon fontSize="small" />
                  </Avatar>
                  <ListItemText
                    primary={file}
                    secondary={t("splitData.UploadCompletedSuccessfully")}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* -------------------------Data Preview------------------------------------- */}
      {parsedSplitCSV.length > 0 && (
        <Card elevation={3}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center" }}
            >
              <PreviewIcon sx={{ mr: 1, color: theme.palette.info.main }} />
              {t("splitData.PreviewOfSplitData")}
              <Chip
                label={`${parsedSplitCSV.length} rows`}
                size="small"
                color="info"
                sx={{ ml: 2 }}
              />
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t("splitData.ShowingFirst10Rows")}
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </CardContent>

          <TableContainer sx={{ maxHeight: 500 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {Object.keys(parsedSplitCSV[0]).map((col) => (
                    <TableCell
                      key={col}
                      sx={{
                        fontWeight: 700,
                        bgcolor: theme.palette.grey[100],
                        borderBottom: `2px solid ${theme.palette.divider}`,
                      }}
                    >
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {parsedSplitCSV.slice(0, 10).map((row, rowIndex) => (
                  <TableRow
                    key={rowIndex}
                    sx={{
                      "&:nth-of-type(odd)": {
                        bgcolor: theme.palette.action.hover,
                      },
                      "&:hover": { bgcolor: theme.palette.action.selected },
                    }}
                  >
                    {Object.keys(parsedSplitCSV[0]).map((col) => (
                      <TableCell key={`${rowIndex}-${col}`}>
                        {row[col]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </Box>
  );
}
